import { DynamicModal } from "src/components/base/components/DynamicModal";
import { createState } from "src/utils/create_zustand_store";
import { Box, Divider, Grid, Stack } from "@mui/material";
import { EventCard } from "../../../feature/homepageFlow/EventCard";
import { pluralize, throttleTime } from "src/utils/utils";
import { TAddOn, TEvent, TPass } from "src/shared/types/globalStotre.types";
import { useGlobalStore } from "../../hooks/useGlobalStore";
import { findEventsByProducts } from "src/components/feature/homepageFlow/utils.homepageFlow";
import { SSText } from "src/components/base/components/SSText";
import React from "react";
import { useSSTheme } from "../../hooks/useSSTheme";
import { useTeamConfig } from "../TeamConfigProvider";

interface TModalState {
  isOpen: boolean;
  failedProducts: TAddOn[];
  successProducts: TAddOn[];
  isAllFailed: boolean;
}

let resolve: any = null;
const useModalState = createState({
  isOpen: false,
  isAllFailed: false,
  failedProducts: [] as TAddOn[],
  successProducts: [] as TAddOn[],
});

export const useAddOnCheckoutErrorModal = () => {
  const modalState = useModalState();

  const execute = async (state: Partial<TModalState>) => {
    return new Promise((res) => {
      resolve = res;
      modalState.set({ isOpen: true, ...state });
    }) as Promise<{ isConfirmed: boolean }>;
  };

  const onClose = () => {
    modalState.set({ isOpen: false });
    throttleTime(200, () => {
      modalState.reset();
    });
  };

  return {
    ...modalState,
    execute,
    onClose,
  };
};

export function AddOnCheckoutErrorModal() {
  const teamConfig = useTeamConfig();
  const IS_QUICK_CHECKOUT = teamConfig?.HOME_PAGE_SETTINGS?.IS_QUICK_CHECKOUT_ENABLED; // prettier-ignore
  const theme = useSSTheme();
  const modalState = useAddOnCheckoutErrorModal();
  const { events } = useGlobalStore();
  const failedEvents = findEventsByProducts(events, modalState.failedProducts);
  const successEvents = findEventsByProducts(
    events,
    modalState.successProducts
  );

  return (
    <DynamicModal
      hideTitleIcon
      isOpen={modalState.isOpen}
      title={"Checkout Error"}
      onClose={() => {
        resolve({ isConfirmed: false });
        modalState.onClose();
      }}
      description={
        <>
          <Grid container spacing={2}>
            {successEvents.map((event: TEvent) => (
              <Grid item xs={12} key={event.eventCode}>
                <EventCard
                  hideTags
                  showFooter
                  event={event}
                  renderFooter={() => {
                    return (
                      <>
                        <Divider />
                        {modalState?.successProducts
                          .filter(
                            (itm) => itm.gameEventCode === event.eventCode
                          )
                          .map((itm, idx) => {
                            return (
                              <React.Fragment key={itm.productId}>
                                {idx !== 0 ? (
                                  <Box px={2}>
                                    <Divider />
                                  </Box>
                                ) : null}
                                <ModalProduct
                                  product={itm}
                                  variant={"success"}
                                />
                              </React.Fragment>
                            );
                          })}
                      </>
                    );
                  }}
                />
              </Grid>
            ))}

            {failedEvents.map((event: TEvent) => (
              <Grid item xs={12} key={event.eventCode}>
                <EventCard
                  hideTags
                  showFooter
                  event={event}
                  renderFooter={() => {
                    return (
                      <>
                        <Divider />
                        {modalState.failedProducts
                          .filter(
                            (itm) => itm.gameEventCode === event.eventCode
                          )
                          .map((itm, idx) => {
                            return (
                              <React.Fragment key={itm.productId}>
                                {idx !== 0 ? (
                                  <Box px={2}>
                                    <Divider />
                                  </Box>
                                ) : null}
                                <ModalProduct
                                  product={itm}
                                  variant={"failed"}
                                />
                              </React.Fragment>
                            );
                          })}
                      </>
                    );
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </>
      }
      slot={{
        footer: {
          style: {
            display: "flex",
          },
        },
        primaryButton: {
          isHidden: modalState?.isAllFailed,
          fullWidth: true,
          label: IS_QUICK_CHECKOUT
            ? "Continue with Available"
            : "Continue To Checkout",
          onClick: () => {
            resolve({ isConfirmed: true });
            modalState.onClose();
          },
        },
        secondaryButton: {
          fullWidth: true,
          label: IS_QUICK_CHECKOUT ? "Try Again" : "Edit Your Cart",
          onClick: () => {
            resolve({ isConfirmed: false });
            modalState.onClose();
          },
        },
      }}
    />
  );
}

function ModalProduct(props: {
  product: TAddOn;
  variant: "failed" | "success";
}) {
  return (
    <Box px={2} py={1.5}>
      <Stack>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <SSText
            variant="body"
            fontWeight={700}
            lineHeight={1.1}
            fontSize={"0.9rem"}
          >
            {props?.product?.productTitle}
          </SSText>
          <SSText
            variant="body"
            fontWeight={400}
            lineHeight={1.1}
            fontSize={"0.9rem"}
          >
            {props?.product?.seatCount}{" "}
            {pluralize("Seat", props?.product?.seatCount)}
          </SSText>
        </Stack>
        <Box mt={0.2}>
          {props?.variant == "failed" ? (
            <SSText variant="caption" color={"error.main"}>
              {props?.product?.errorMessage}
            </SSText>
          ) : null}
          {props?.variant == "success" ? (
            <SSText variant="caption" color={"success.main"}>
              Successfully Added
            </SSText>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}
